export interface XIRRCashFlow {
  date: Date;
  amount: number;
}

/**
 * Calculate NPV and its derivative for a given rate
 */
const calculateNPVAndDerivative = (
  flowsWithYears: Array<{ amount: number; years: number }>,
  rate: number
): { npv: number; dnpv: number } => {
  let npv = 0;
  let dnpv = 0;

  flowsWithYears.forEach(flow => {
    const factor = (1 + rate) ** flow.years;
    npv += flow.amount / factor;
    dnpv -= flow.years * flow.amount / (factor * (1 + rate));
  });

  return { npv, dnpv };
};

/**
 * Attempt XIRR calculation with a specific initial guess
 */
const tryXIRRCalculation = (
  flowsWithYears: Array<{ amount: number; years: number }>,
  guess: number
): number | null => {
  let rate = guess;
  const maxIterations = 200;
  const tolerance = 1e-7;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < maxIterations; i++) {
    const { npv, dnpv } = calculateNPVAndDerivative(flowsWithYears, rate);

    if (Math.abs(npv) < tolerance) {
      return rate;
    }

    if (Math.abs(dnpv) < tolerance) {
      // Derivative too small, can't continue
      return null;
    }

    const newRate = rate - npv / dnpv;

    // Check for convergence
    if (Math.abs(newRate - rate) < tolerance) {
      return newRate;
    }

    rate = newRate;

    // Prevent rate from going too extreme
    // Allow very high rates for extreme value scenarios (e.g., $12 trillion unrealized value)
    if (rate < -0.99) {
      rate = -0.99;
    }
    if (rate > 1000) {
      rate = 1000;
    }
  }

  return 0; // Failed to converge
};

/**
 * Calculate XIRR (Extended Internal Rate of Return) using Newton-Raphson method
 * @param cashFlows Array of cash flows with dates and amounts (negative = outflow, positive = inflow)
 * @param guess Initial guess for the rate (default 0.1 = 10%)
 * @returns The XIRR as a decimal (e.g., 0.25 = 25%) or null if calculation fails
 */
const XIRR = (cashFlows: XIRRCashFlow[], guess: number = 0.1): number | null => {
  if (cashFlows.length < 2) {
    return null;
  }

  // Check for valid cash flow pattern (need both positive and negative flows for IRR)
  const hasNegative = cashFlows.some(cf => cf.amount < 0);
  const hasPositive = cashFlows.some(cf => cf.amount > 0);
  if (!hasNegative || !hasPositive) {
    return null;
  }

  const sortedFlows = [...cashFlows].sort((a, b) => a.date.getTime() - b.date.getTime());
  const firstDate = sortedFlows[0].date;

  // Convert dates to years from first date using actual/365 day count convention
  const flowsWithYears = sortedFlows.map(cf => ({
    amount: cf.amount,
    years: (cf.date.getTime() - firstDate.getTime()) / (365 * 24 * 60 * 60 * 1000),
  }));

  // Seed around 1, with outliers for allowing abnormally high IRR for special cases.
  const guesses = [guess, 0.0, 0.5, -0.5, 1.0, -1.0, 2.0, 0.3, 270.0, -120.0, 75.0];

  let result: number | null = null;
  guesses.some(initialGuess => {
    const attempt = tryXIRRCalculation(flowsWithYears, initialGuess);
    if (attempt !== null) {
      result = attempt;
      return true;
    }
    return false;
  });

  return result; // null when no guess converged
};

export default XIRR;
